import { Link } from "gatsby";
import SingleProductTile from "./plp/SingleProductTile";
import styled from "styled-components";
import ScrollableOverflow from "./ScrollableOverflow";
import { useCallback, useState } from "react";
import { ResizeHandler } from "use-resize-observer";
import useThrottledResizeObserver from "../hooks/useThrottledResizeObserver";

const ProductCarouselHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: var(--spacing-1_5);

  p,
  h2 {
    margin: 0;
  }

  a {
    border-bottom: 2px solid var(--indigo-blue);
    transition: all 0.3s ease-in-out;
    cursor: pointer;

    &:hover {
      color: var(--indigo-blue-60);
      border-color: var(--indigo-blue-60);
    }
  }
`;

const ProductTileWrapper = styled.div`
  flex-direction: column;
  width: 213px;
  margin-right: var(--spacing-1);

  &:last-child {
    margin-right: 0;
  }

  @media (min-width: 933px) {
    width: 300px;
    margin-right: var(--spacing-1_5);
  }
`;

type ProductCarouselProps = {
  headline?: {
    childMarkdownRemark: {
      html: string;
    };
  };
  link?: {
    text: string;
    to: {
      path?: string;
      slug?: string;
    };
  };
  products: any[];
  currency: {
    symbol: string;
    type: string;
  };
};

const ProductCarousel = ({
  headline,
  link,
  products,
  currency,
}: ProductCarouselProps) => {
  const [cardWidth, setCardWidth] = useState(213);

  const calculateCardWidth = useCallback<ResizeHandler>(
    ({ width: resizedWrapperWidth }) => {
      const desktop =
        window.matchMedia && window.matchMedia("(min-width: 768px)").matches;

      if (!desktop) {
        const wrapperWidth = resizedWrapperWidth || 213;
        const gutter = 16 * 1.5;
        setCardWidth((wrapperWidth - gutter) * 0.5);
      } else {
        const wrapperWidth = resizedWrapperWidth || 300;
        const gutter = 16 * 1.5 * 3;
        setCardWidth((wrapperWidth - gutter) * 0.25);
      }
    },
    [],
  );

  const { ref: wrapperRef } = useThrottledResizeObserver({
    onResize: calculateCardWidth,
  });

  return (
    <div ref={wrapperRef}>
      <ProductCarouselHeader>
        <h2
          className="typography-headline2"
          dangerouslySetInnerHTML={{
            __html: headline?.childMarkdownRemark?.html || "",
          }}
        />
        {link && (
          <Link
            to={link.to?.path || link.to?.slug || ""}
            className="typography-label2"
          >
            {link.text}
          </Link>
        )}
      </ProductCarouselHeader>
      <ScrollableOverflow bleedRight={true} bleedLeft={true}>
        {products?.map((product) => {
          const item = {
            size: "small",
            content: {
              ...product,
            },
          };
          return (
            <ProductTileWrapper
              key={product.id}
              style={{
                width: cardWidth,
              }}
            >
              <SingleProductTile
                item={item}
                location="Product Carousel"
                currency={currency}
              />
            </ProductTileWrapper>
          );
        })}
      </ScrollableOverflow>
    </div>
  );
};

export default ProductCarousel;
